import { Injectable } from '@angular/core';
import {
  CreatePerson,
  FullPerson,
  Person,
  PersonEditorCompanyConfig,
  PersonEditorForms
} from '../models/person';
import {
  EmployeeTax,
  EmployeeTaxes,
  EmployeeTaxExemptions,
  StateTaxExemption
} from '../models/employee-tax';
import { UntypedFormArray } from '@angular/forms';
import { CompanyTaxCodeConfig } from '../models/tax-config';
import { JobDetailsForm, TaxesForm } from '../state/PersonEditorState/FormTypes';
import {
  IPersonEditorState,
  PersonEditorState
} from '../state/PersonEditorState/PersonEditor.state';
import { parseDatePickerValue } from '../utils/datepicker-value-parser';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { ApiResponse } from '../models/api-response';
import { FlattenResponseMessages, GetResponseModalStyle } from '../utils/api-response-helper';
import { getObjectDeltas } from '../utils/object-deltas';
import { StateContext } from '@ngxs/store';
import { PeopleService } from './people.service';
import { ToastService } from './toast.service';
import { ModalService } from './modal.service';
import { AuditService } from './audit.service';
import { patch } from '@ngxs/store/operators';
import {
  LoadUniversalPersonLists,
  SetEmergencyContacts,
  SetLaborAllocations
} from '../state/PersonEditorState/PersonEditor.actions';
import { forkJoin, Observable, of } from 'rxjs';
import { EmploymentTypes } from '../models/employment-types';
import { EmployeeDeduction } from '../models/employee-deductions';
import { EmployeeEarning } from '../models/employee-earning';
import { EmergencyContact } from '../models/emergency-contact';
import { EmployeeLaborAllocation } from '../models/labor-allocation';

@Injectable({
  providedIn: 'root'
})

// responsible for all of the people editor save logic
export class PersonSaveService {
  constructor(
    private peopleService: PeopleService,
    private toastService: ToastService,
    private modalService: ModalService,
    private auditService: AuditService
  ) {}

  // assign values from form to the person tax data model
  public setTaxDataModel(
    person: Person,
    jobDetailsForm: JobDetailsForm,
    taxesForm: TaxesForm,
    taxesInput: EmployeeTaxes,
    taxCodes: CompanyTaxCodeConfig[]
  ) {
    const ctrls = structuredClone(taxesForm);
    const taxes = structuredClone(taxesInput);
    // pass back all federal & state tax elections we got
    // set anything not selected in the UI as inactive
    taxes.FedAndState.forEach(x => (x.IsActive = false));

    // federal
    const fedConfig = taxCodes.find(x => x.EntityType === 'F');
    let fedTax = taxes.FedAndState.find(x => x.TaxCode === fedConfig.TaxCode);

    if (!fedTax) {
      fedTax = new EmployeeTax();
      fedTax.TaxCode = fedConfig.TaxCode;
      taxes.FedAndState.push(fedTax);
    }

    fedTax.IsActive = true;

    // for non W2 employees where taxes tab is disabled
    // tax tab load logic does not get to fire and set default value for ctrls.FederalFilingStatus
    if (!ctrls.FederalFilingStatus) {
      const stateAbbrev = jobDetailsForm.WorksIn;
      const config = taxCodes.find(x => x.StateAbbreviation === stateAbbrev && x.TaxType === 'I');
      const def = config.FilingStatuses.find(x => x.IsDefault);
      if (def && !ctrls.IsExemptFromWithholding) {
        ctrls.FederalFilingStatus = def.Id;
      } else if (def && ctrls.IsExemptFromWithholding) {
        // even if fed is exempt, and control shows null,
        // we still need to pass 'S' to the server. because taxes.
        fedTax.FilingStatus = 'S';
      }
    }

    fedTax.FilingStatus = ctrls.FederalFilingStatus || fedTax.FilingStatus;
    fedTax.WithholdingAllowance = ctrls.FederalWithholdingAllowance;
    fedTax.AdditionalWithholdingType =
      ctrls.FederalAddlWithholdingType !== 'null' ? ctrls.FederalAddlWithholdingType : '';
    fedTax.AdditionalWithholdingAmount = ctrls.FederalAddlWithholdingValue || 0;

    fedTax.IsExemptFromWithholding = ctrls.IsExemptFromWithholding;
    fedTax.W4Version = parseInt(ctrls.W4Version);
    fedTax.IsHigherWithholding = ctrls.IsHigherWithholding;
    fedTax.ChildTaxCredit = ctrls.ChildTaxCredit;
    fedTax.OtherIncome = ctrls.OtherIncome;
    fedTax.WageDeduction = ctrls.WageDeduction;

    // states
    const stateElections = taxesForm.WithHoldingState;

    if (stateElections.indexOf('Both') > -1 || stateElections === person.WorksIn) {
      // save work state tax elections
      const workStateConfig = taxCodes.find(
        x => x.EntityType === 'S' && x.StateAbbreviation === person.WorksIn
      );
      let workStateTax = taxes.FedAndState.find(x => x.TaxCode === workStateConfig.TaxCode);

      if (!workStateTax) {
        workStateTax = new EmployeeTax();
        workStateTax.TaxCode = workStateConfig.TaxCode;
        workStateTax.StateAbbreviation = workStateConfig.StateAbbreviation;
        taxes.FedAndState.push(workStateTax);
      }

      workStateTax.IsActive = true;
      workStateTax.FilingStatus = ctrls.StateFilingStatus;
      workStateTax.StateAbbreviation = workStateConfig.StateAbbreviation;
      workStateTax.WithholdingAllowance = ctrls.StateWithholdingAllowance;
      workStateTax.AdditionalAllowances = ctrls.StateAddlAllowances;
      workStateTax.AdditionalWithholdingType =
        ctrls.StateAddlWithholdingType !== 'null' ? ctrls.StateAddlWithholdingType : '';
      workStateTax.AdditionalWithholdingAmount = ctrls.StateAddlWithholdingValue || 0;
    }

    if (
      (stateElections.indexOf('Both') > -1 || stateElections === person.State) &&
      person.State !== person.WorksIn
    ) {
      // save home state tax elections

      const homeStateConfig = taxCodes.find(
        x => x.EntityType === 'S' && x.StateAbbreviation === person.State
      );
      if (homeStateConfig) {
        let homeStateTax = taxes.FedAndState.find(x => x.TaxCode === homeStateConfig.TaxCode);

        if (!homeStateTax) {
          homeStateTax = new EmployeeTax();
          homeStateTax.TaxCode = homeStateConfig.TaxCode;
          homeStateTax.StateAbbreviation = homeStateConfig.StateAbbreviation;
          taxes.FedAndState.push(homeStateTax);
        }

        homeStateTax.IsActive = true;
        homeStateTax.FilingStatus = ctrls.AddlStateFilingStatus;
        homeStateTax.StateAbbreviation = homeStateConfig.StateAbbreviation;
        homeStateTax.WithholdingAllowance = ctrls.AddlStateWithholdingAllowance;
        homeStateTax.AdditionalAllowances = ctrls.AddlStateAddlAllowances;
        homeStateTax.AdditionalWithholdingType =
          ctrls.AddlStateAddlWithholdingType !== 'null' ? ctrls.AddlStateAddlWithholdingType : '';
        homeStateTax.AdditionalWithholdingAmount = ctrls.AddlStateAddlWithholdingValue || 0;
      }
    }

    // locals
    taxes.Locals = [];
    const locals = taxesForm.Locals;
    locals.forEach(ctrl => {
      if (ctrl.LocalTypeId) {
        taxes.Locals.push({
          LocalTypeId: ctrl.LocalTypeId,
          TaxCode: ctrl.TaxCode || null,
          FilingStatus: ctrl.FilingStatus || null,
          WithholdingAllowances: ctrl.WithholdingAllowances || 0
        });
      }
    });
    return taxes;
  }

  /**
   *  get model data from tax exemptions form
   * @param state
   */
  public GetTaxExemptions(state: IPersonEditorState): EmployeeTaxExemptions {
    const taxesForm = state.Forms.TaxesForm.model;
    // exemptions
    const e = new EmployeeTaxExemptions();
    e.HasSSExemption = taxesForm.HasSSExemption || false;
    e.HasMEDCExemption = taxesForm.HasMEDCExemption || false;
    e.HasSUIExemption = taxesForm.HasSUIExemption || false;
    e.HasFUTAExemption = taxesForm.HasFUTAExemption || false;
    e.FUTAExemptionReason = taxesForm.FUTAExemptionReason;
    e.HasVisa = taxesForm.J1F1Visa == '1';
    e.VisaExpirationDate = parseDatePickerValue(taxesForm.ExpireDate);

    const additional = this.getStateTaxExemptions(state);

    if (!additional) {
      return e;
    }

    e.EmployeeStateTaxExemptionsByState = structuredClone(
      state.TaxExemptions.EmployeeStateTaxExemptionsByState
    );

    const exemption = e.EmployeeStateTaxExemptionsByState?.find(
      x => x.EmployeeTaxExemptionState == additional.EmployeeTaxExemptionState
    );

    for (let add in additional.EmployeeStateTaxExemptions) {
      exemption.EmployeeStateTaxExemptions[add] = taxesForm[add] || false;
    }

    return e;
  }

  // set model data from person details form
  // FIXME: move to init service
  // rename to something like setPersonDetailsDataModel
  public SavePersonalDetails(p: Person, state: IPersonEditorState) {
    const controls = state.Forms.PersonalDetailsForm.model;
    p.EmployeeId = controls.EmploymentTypeId;
    p.UseEIN = controls.UseEIN === '1';
    if (state.FederalIdChanged) {
      p.FederalIdentifier = controls.FederalIdentifier !== null ? controls.FederalIdentifier : '';
    }
    p.EmploymentTypeId = controls.EmploymentTypeId;
    p.IsCompany = controls.IsCompany || false;
    p.FirstName = controls.FirstName?.trim();
    p.MiddleName = controls.MiddleName?.trim();
    p.LastName = controls.LastName?.trim();
    p.Birthday = parseDatePickerValue(controls.Birthday);
    p.PersonalEmailAddress = controls.PersonalEmailAddress;
    p.HomePhoneNumber = controls.HomePhoneNumber;
    p.MobilePhoneNumber = controls.MobilePhoneNumber;
    p.Gender = controls.Gender;
    p.MaritalStatus = controls.MaritalStatus;
    p.Race = controls.Race;
    p.StreetAddress = controls.StreetAddress;
    p.StreetAddress2 = controls.StreetAddress2;
    p.City = controls.City;
    p.State = controls.State;
    if (!controls.IsNonUSAddress) {
      p.PostalCode = controls.PostalCode ? controls.PostalCode.slice(0, 5) : '';
      p.PostalCodeExtension =
        controls.PostalCode && controls.PostalCode.trim() ? controls.PostalCode.slice(5) : '';
    } else {
      p.PostalCode = controls.PostalCode;
    }
    p.Country = controls.Country;
    p.IsNonUSAddress = controls.IsNonUSAddress;

    // task 34428 - scrub first & middle name if 'use company name' flag is checked
    if (p.IsCompany) {
      p.FirstName = p.MiddleName = '';
    }
    return p;
  }

  // set model data from job details form
  // FIXME: move to init service
  // rename to something like setJobDetailsDataModel
  public SaveJobDetails(p: Person, state: IPersonEditorState) {
    const controls = structuredClone(state.Forms.JobDetailsForm.model);

    // on new person creation, default original hire date to same as hire date
    if (!p.Id && controls.HireDate && !controls.OriginalHireDate) {
      controls.OriginalHireDate = controls.HireDate;
    }

    p.EmployeeId = controls.EmployeeId || 0;
    p.HireDate = parseDatePickerValue(controls.HireDate);
    p.ShouldReportAsNewHire = controls.ShouldReportAsNewHire == '1';
    p.WorksIn = controls.WorksIn;
    p.OKToAutoPay = controls.OKToAutoPay == '1';

    // FIXME: this dereferncing isn't entirely safe.  there may be scenarios
    // where we won't be able to resolve the calendar from the person's location
    // and pay frequency, under exotic conditions when editing an already-existing
    // person record.  Revisit this for potential null-ref errors   --nk 2025-02-18
    const locId = controls.Location ?? 0;
    const calendarId = state.CompanyConfigs.Locations[locId].Calendars.find(
      c => c.Frequency == controls.PayFrequency
    ).CalendarId;

    // Bug 40362: ensure the calendar id is set as well as the pay frequency  --nk 2025-02-18
    p.CalendarId = calendarId;
    p.PayFrequency = controls.PayFrequency;

    p.Location = controls.Location;
    p.Division = controls.Division;
    p.Department = controls.Department;
    p.JobTitle = controls.JobTitle;
    p.IsSeasonal = controls.IsSeasonal;
    p.FullTimePartTime = controls.FullTimePartTime;
    p.IsOwnerOfficer = controls.IsOwnerOfficer;
    p.SOCCode = controls.SOCCode;
    p.OccupationId = !controls.OccupationId ? 0 : controls.OccupationId;
    p.BasisOfPayment = !controls.BasisOfPayment ? 0 : controls.BasisOfPayment;
    p.WorkEmailAddress = controls.WorkEmailAddress;
    p.WorkPhoneNumber = controls.WorkPhoneNumber;
    p.OriginalHireDate = parseDatePickerValue(controls.OriginalHireDate);
    p.IncludeNonW2InWorkersComp = controls.IncludeNonW2InWorkersComp;
    p.WorkersCompCode = controls.WorkersCompCode;
    p.WorkersCompOwnerStatus = parseInt(controls.WorkersCompOwnerStatus);
    p.WorkersCompLimitId = controls.WorkersCompLimitId;
    return p;
  }

  public CreatePerson(
    fullPerson: FullPerson,
    ctx: StateContext<IPersonEditorState>,
    clientCode: string
  ) {
    fullPerson.Person.Id = 0;
    const newPerson = new CreatePerson(fullPerson.Person, fullPerson.Taxes);
    return this.peopleService.createPerson(clientCode, newPerson).pipe(
      tap((response: ApiResponse<Person>) => {
        ctx.setState(
          patch<IPersonEditorState>({
            Person: response.Model,
            PersonLists: patch({
              Taxes: fullPerson.Taxes
            })
          })
        );
      }),
      mergeMap((response: ApiResponse<Person>) => {
        this.toastService.show('Save successful');
        const msg = FlattenResponseMessages(response.Messages);
        if (msg?.Body) {
          ctx.patchState({
            ModalStyle: GetResponseModalStyle(response.Messages),
            AlertModalMessage: msg
          });
        }

        const ignore = ['IntegrityHash', 'InvalidToPay', 'InvalidToPayDetails'];

        // if any of the below property values are 0, do not write to audit log
        const props = [
          'BasisOfPayment',
          'CalendarId',
          'Department',
          'Division',
          'Location',
          'OccupationId'
        ];
        let state = ctx.getState();
        props.forEach(prop => {
          if (state.Person[prop] == 0) {
            ignore.push(prop);
          }
        });

        const deltas = getObjectDeltas(null, state.Person, ignore);

        // Make sure we don't pass in the client code here, it will clobber the actual client code
        // when we merge the two objects together for sending to the audit endpoint
        // bug #31852
        delete deltas.ClientCode;

        this.auditService.auditEmployee(
          `employee-created`,
          {
            ClientCode: clientCode,
            PersonId: state.Person.Id,
            EmployeeNumber: state.Person.EmployeeId,
            EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
          },
          deltas
        );

        // audit taxes
        for (let i = 0; i < state.PersonLists.Taxes.FedAndState.length; i++) {
          // FIXME: check if this.taxes.FedAndState[i].StateAbbreviation is undefined
          const contextName =
            state.PersonLists.Taxes.FedAndState[i].StateAbbreviation != 'FED'
              ? `State > ${state.PersonLists.Taxes.FedAndState[i].StateAbbreviation}`
              : 'Federal';

          const fedStateTaxDeltas = getObjectDeltas(
            null,
            state.PersonLists.Taxes.FedAndState[i],
            ['IntegrityHash'],
            contextName
          );
          if (Object.keys(fedStateTaxDeltas).length > 0) {
            this.auditService.auditEmployee(
              `employee-tax-created`,
              {
                ClientCode: clientCode,
                PersonId: state.Person.Id,
                EmployeeNumber: state.Person.EmployeeId,
                EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
              },
              fedStateTaxDeltas
            );
          }
        }
        ctx.setState(
          patch<IPersonEditorState>({
            PersonLists: patch({
              Taxes: patch({
                Locals: state.PersonLists.Taxes.Locals || []
              })
            })
          })
        );
        state = ctx.getState();
        for (let i = 0; i < state.PersonLists.Taxes.Locals.length; i++) {
          const contextName = state.CompanyConfigs.LocalTaxs.find(
            x => x.LocalTypeId == state.PersonLists.Taxes.Locals[i].LocalTypeId
          ).TaxLabel;
          const localTaxDeltas = getObjectDeltas(
            null,
            state.PersonLists.Taxes.Locals[i],
            ['IntegrityHash'],
            contextName
          );
          if (Object.keys(localTaxDeltas).length > 0) {
            this.auditService.auditEmployee(
              `employee-tax-updated`,
              {
                ClientCode: clientCode,
                PersonId: state.Person.Id,
                EmployeeNumber: state.Person.EmployeeId,
                EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
              },
              localTaxDeltas
            );
          }
        }
        return this._fetchAndCreateNewPersonLists(fullPerson, ctx, clientCode);
      })
    );
  }

  private _fetchAndCreateNewPersonLists(
    p: FullPerson,
    ctx: StateContext<IPersonEditorState>,
    clientCode: string
  ) {
    return ctx.dispatch(new LoadUniversalPersonLists(clientCode, p.Person.Id)).pipe(
      mergeMap(() => {
        const state = ctx.getState();
        // for earnings & deductions, we are only updating - never inserting. assign integrity hashes
        p.Earnings = p.Earnings.map(x => {
          const earning = state.PersonLists.Earnings.find(e => e.Id === x.Id);
          return {
            ...x,
            IntegrityHash: earning.IntegrityHash
          };
        });

        p.Deductions = p.Deductions.map(x => {
          const deduction = state.PersonLists.Deductions.find(d => d.Id === x.Id);
          return {
            ...x,
            IntegrityHash: deduction.IntegrityHash
          };
        });
        return this._createDeductionRates(p.Deductions, false, ctx, clientCode);
      }),
      mergeMap(() => {
        return this._createEarningRates(p.Earnings, false, ctx, clientCode);
      }),
      mergeMap(() => {
        return this._createEmergencyContacts(p.EmergencyContacts, clientCode, p.Person.Id);
      }),
      mergeMap(c => {
        let emergencyContacts = [];
        const state = ctx.getState();
        c.forEach(response => {
          const deltas = getObjectDeltas(null, response.Model, ['IntegrityHash']);
          this.auditService.auditEmployee(
            `employee-emergency-contact-created`,
            {
              ClientCode: clientCode,
              PersonId: state.Person.Id,
              EmployeeNumber: state.Person.EmployeeId,
              EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
            },
            deltas
          );

          emergencyContacts.push(response.Model);
        });
        return ctx.dispatch(new SetEmergencyContacts(emergencyContacts));
      }),
      mergeMap(() => {
        if (p.Person.EmploymentTypeId == EmploymentTypes.W2) {
          return this._createTaxExemptions(p.TaxExemptions, ctx, clientCode);
        }
        return of([]);
      }),
      mergeMap(() => {
        const state = ctx.getState();
        if (p.LaborAllocations && p.LaborAllocations.length > 0) {
          return this.peopleService
            .updateLaborAllocations(clientCode, state.Person.Id, p.LaborAllocations)
            .pipe(map(response => response.Model));
        } else {
          return of([]);
        }
      }),
      mergeMap(response => {
        return ctx.dispatch(new SetLaborAllocations(response));
      }),
      tap(() => {
        const state = ctx.getState();
        const laborAllocations = state.PersonLists.LaborAllocations;
        const jobCodes = state.CompanyConfigs.JobCodes;
        laborAllocations.forEach(r => {
          const contextName = jobCodes.find(x => x.Id == r.JobCodeId).Value;
          const deltas = getObjectDeltas(null, r, ['IntegrityHash'], contextName);
          this.auditService.auditEmployee(
            `employee-labor-allocation-created`,
            {
              ClientCode: clientCode,
              PersonId: state.Person.Id,
              EmployeeNumber: state.Person.EmployeeId,
              EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
            },
            deltas
          );
        });
      }),
      catchError(err => {
        return of();
      })
    );
  }

  public getStateTaxExemptions(state: IPersonEditorState): StateTaxExemption {
    const worksIn = state.Forms.JobDetailsForm.model.WorksIn;

    if (!worksIn || !state.TaxExemptions) {
      return null;
    }

    return state.TaxExemptions.EmployeeStateTaxExemptionsByState?.find(
      x => x.EmployeeTaxExemptionState == worksIn
    );
  }

  private _createDeductionRates(
    deductions: EmployeeDeduction[],
    showSuccessMsg: boolean = false,
    ctx: StateContext<IPersonEditorState>,
    clientCode
  ) {
    const state = ctx.getState();
    if (deductions && deductions.length > 0) {
      const obs = [];
      deductions.forEach(d => {
        // if user is not allowed to edit rates, clear rates collection
        const config = state.CompanyConfigs.Deductions.find(x => x.Id === d.Id);
        if (!config.CanHaveRates) {
          d.Rates = [];
        }

        obs.push(this.peopleService.updateEmployeeDeduction(clientCode, state.Person.Id, d));
      });

      return forkJoin<any>(obs).pipe(
        mergeMap(() => {
          deductions.forEach(d => {
            const config = state.CompanyConfigs.Deductions.find(x => x.Id == d.Id);
            const deltas = getObjectDeltas(
              null,
              d,
              ['Rates', 'IntegrityHash', 'LoanBalance', 'Valid'],
              config.Name
            );
            this.auditService.auditEmployee(
              `employee-deduction-updated`,
              {
                ClientCode: clientCode,
                PersonId: state.Person.Id,
                EmployeeNumber: state.Person.EmployeeId,
                EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
              },
              deltas
            );

            if (d.Rates && d.Rates.length > 0) {
              d.Rates.forEach(r => {
                const rDeltas = getObjectDeltas(
                  null,
                  r,
                  ['LastChangedOn', 'LastEditorId', 'IntegrityHash'],
                  config.Name
                );
                this.auditService.auditEmployee(
                  `employee-deduction-rate-created`,
                  {
                    ClientCode: clientCode,
                    PersonId: state.Person.Id,
                    EmployeeNumber: state.Person.EmployeeId,
                    EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
                  },
                  rDeltas
                );
              });
            }
          });

          return this.peopleService.getEmployeeDeductions(clientCode, state.Person.Id).pipe(
            tap(data => {
              ctx.setState(
                patch<IPersonEditorState>({
                  PersonLists: patch({
                    Deductions: data
                  })
                })
              );

              if (showSuccessMsg) {
                this.toastService.show('Save successful');
              }
            })
          );
        })
      );
    }
    return of([]);
  }

  private _createEarningRates(
    earnings: EmployeeEarning[],
    showSuccessMsg: boolean = false,
    ctx: StateContext<IPersonEditorState>,
    clientCode: string
  ) {
    const state = ctx.getState();
    if (earnings && earnings.length > 0) {
      const obs = [];
      earnings.forEach(e => {
        // if user is not allowed to edit rates, clear rates collection
        const config = state.CompanyConfigs.Earnings.find(x => x.Id === e.Id);
        if (!config.CanHaveRates) {
          e.Rates = [];
        }

        obs.push(this.peopleService.updateEarning(clientCode, state.Person.Id, e));
      });

      return forkJoin<any>(obs).pipe(
        mergeMap(() => {
          earnings.forEach(e => {
            const config = state.CompanyConfigs.Earnings.find(x => x.Id == e.Id);
            const deltas = getObjectDeltas(
              null,
              e,
              ['Rates', 'IntegrityHash', 'Valid'],
              config.Name
            );
            this.auditService.auditEmployee(
              `employee-earning-updated`,
              {
                ClientCode: clientCode,
                PersonId: state.Person.Id,
                EmployeeNumber: state.Person.EmployeeId,
                EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
              },
              deltas
            );

            if (e.Rates && e.Rates.length > 0) {
              e.Rates.forEach(r => {
                const rDeltas = getObjectDeltas(
                  null,
                  r,
                  ['LastChangedOn', 'LastEditorId', 'IntegrityHash'],
                  config.Name
                );
                this.auditService.auditEmployee(
                  `employee-earning-rate-created`,
                  {
                    ClientCode: clientCode,
                    PersonId: state.Person.Id,
                    EmployeeNumber: state.Person.EmployeeId,
                    EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
                  },
                  rDeltas
                );
              });
            }
          });
          return this.peopleService.getEmployeeEarnings(clientCode, state.Person.Id).pipe(
            tap(data => {
              ctx.setState(
                patch<IPersonEditorState>({
                  PersonLists: patch({
                    Earnings: data
                  })
                })
              );

              if (showSuccessMsg) {
                this.toastService.show('Save successful');
              }
            })
          );
        })
      );
    }
    return of([]);
  }

  private _createEmergencyContacts(
    emergencyContacts: EmergencyContact[],
    clientCode: string,
    personId: number
  ): Observable<any> {
    if (emergencyContacts && emergencyContacts.length > 0) {
      const obs = [];
      emergencyContacts.forEach(c => {
        obs.push(this.peopleService.createEmergencyContact(clientCode, personId, c));
      });

      return forkJoin<any>(obs);
    } else {
      return of([]);
    }
  }

  private _createTaxExemptions(
    exemptions: EmployeeTaxExemptions,
    ctx: StateContext<IPersonEditorState>,
    clientCode: string
  ) {
    let state = ctx.getState();
    if (exemptions) {
      return this.peopleService
        .updateEmployeeTaxExemptions(clientCode, state.Person.Id, exemptions)
        .pipe(
          tap((response: ApiResponse<EmployeeTaxExemptions>) => {
            ctx.setState(
              patch<IPersonEditorState>({
                TaxExemptions: response.Model
              })
            );
            state = ctx.getState();

            let deltas = getObjectDeltas(null, response.Model, [
              'IntegrityHash',
              'EmployeeStateTaxExemptionsByState'
            ]);

            let stateDeltas = this.getStateTaxExemptionDeltas(null, state.TaxExemptions);

            deltas = { ...stateDeltas, ...deltas };

            this.auditService.auditEmployee(
              `employee-tax-exemptions-updated`,
              {
                ClientCode: clientCode,
                PersonId: state.Person.Id,
                EmployeeNumber: state.Person.EmployeeId,
                EmployeeName: `${state.Person.LastName}, ${state.Person.FirstName}`
              },
              deltas
            );
          })
        );
    }
    return of([]);
  }
  public getStateTaxExemptionDeltas(
    originalExemptions: EmployeeTaxExemptions,
    updatedExemptions: EmployeeTaxExemptions
  ) {
    let deltas = {};
    // Handle the cases where a person suddenly has or has removed the EmployeeTaxExemptionsByState
    // collection by grabbing whichever is not null
    var byState =
      updatedExemptions?.EmployeeStateTaxExemptionsByState == null
        ? originalExemptions.EmployeeStateTaxExemptionsByState
        : updatedExemptions.EmployeeStateTaxExemptionsByState;

    if (byState == null) {
      return deltas;
    }

    // Grab each state string
    var states = byState.map(exemption => exemption.EmployeeTaxExemptionState);

    for (let state of states) {
      // Get the before/after values for each state
      var original = originalExemptions?.EmployeeStateTaxExemptionsByState?.find(
        t => t.EmployeeTaxExemptionState == state
      );
      var updated = updatedExemptions?.EmployeeStateTaxExemptionsByState?.find(
        t => t.EmployeeTaxExemptionState == state
      );

      // Check for deltas between each state collection
      var delta = getObjectDeltas(
        original?.EmployeeStateTaxExemptions ?? null,
        updated?.EmployeeStateTaxExemptions ?? null,
        [],
        `EmployeeStateTaxExemptions > ${state}`
      );

      // If we have any, append them to the deltas object
      if (delta != null) {
        deltas = { ...delta, ...deltas };
      }
    }

    return deltas;
  }
}
